// Per-tour ORDER + QUOTES cache. Both are DURABLE in DynamoDB on each tour's
// TOUR#<slug> record (`order` = the artifact-ULID arrangement, `quotes` = the
// per-tour quotes). This module is the synchronous, in-memory view of that content
// used during render (tours.ts `orderTourSlides`; the quote-slide builder/grid via
// useTours); the actual network reads/writes live in api.ts and are driven by
// useTours (hydrateOrders on load, putTour on reorder / quote-slide writes).
//
// localStorage is kept as a WRITE-THROUGH resilience cache only: it lets an order
// (or the quotes) survive a reload even if a server write is briefly in flight or
// the tours API hiccups. The server is authoritative — hydrateOrders overwrites the
// cache from the loaded TOUR# records.
//
//   localStorage["tourOrder:<slug>"]       = JSON.stringify(string[])      // slide ids (ULIDs + qs_*)
//   localStorage["tourQuotes:<slug>"]      = JSON.stringify(Quote[])       // LEGACY per-tour quotes
//   localStorage["tourQuoteSlides:<slug>"] = JSON.stringify(QuoteSlide[])  // per-tour quote slides
//
// All functions are safe no-ops when localStorage is unavailable (private mode).

import type { Quote, QuoteSlide } from "./types";

const KEY_PREFIX = "tourOrder:";
const QUOTES_KEY_PREFIX = "tourQuotes:";
const QUOTE_SLIDES_KEY_PREFIX = "tourQuoteSlides:";

/** In-memory orders hydrated from the server (slug -> ordered slide ids). */
const serverOrders = new Map<string, string[]>();

/** In-memory quotes hydrated from the server (slug -> the tour's LEGACY quotes). */
const serverQuotes = new Map<string, Quote[]>();

/** In-memory quote slides hydrated from the server (slug -> the tour's quote slides). */
const serverQuoteSlides = new Map<string, QuoteSlide[]>();

function keyFor(slug: string): string {
  return `${KEY_PREFIX}${slug}`;
}

function quotesKeyFor(slug: string): string {
  return `${QUOTES_KEY_PREFIX}${slug}`;
}

function quoteSlidesKeyFor(slug: string): string {
  return `${QUOTE_SLIDES_KEY_PREFIX}${slug}`;
}

/** Best-effort access to localStorage (may be absent/blocked). */
function store(): Storage | null {
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    return null;
  }
}

function readLocal(slug: string): string[] {
  const s = store();
  if (!s) return [];
  try {
    const raw = s.getItem(keyFor(slug));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((x): x is string => typeof x === "string");
  } catch {
    return [];
  }
}

function writeLocal(slug: string, orderedIds: string[]): void {
  const s = store();
  if (!s) return;
  try {
    s.setItem(keyFor(slug), JSON.stringify(orderedIds));
  } catch {
    // Quota / private mode — ignore; the server copy is authoritative.
  }
}

/** Coerce an unknown into a clean `Quote[]` (defensive against stale localStorage). */
function sanitizeQuotes(raw: unknown): Quote[] {
  if (!Array.isArray(raw)) return [];
  const out: Quote[] = [];
  for (const q of raw) {
    if (!q || typeof q !== "object") continue;
    const rec = q as Record<string, unknown>;
    if (typeof rec.text !== "string") continue;
    out.push(
      typeof rec.attribution === "string"
        ? { attribution: rec.attribution, text: rec.text }
        : { text: rec.text },
    );
  }
  return out;
}

function readLocalQuotes(slug: string): Quote[] {
  const s = store();
  if (!s) return [];
  try {
    const raw = s.getItem(quotesKeyFor(slug));
    if (!raw) return [];
    return sanitizeQuotes(JSON.parse(raw));
  } catch {
    return [];
  }
}

function writeLocalQuotes(slug: string, quotes: Quote[]): void {
  const s = store();
  if (!s) return;
  try {
    s.setItem(quotesKeyFor(slug), JSON.stringify(quotes));
  } catch {
    // Quota / private mode — ignore; the server copy is authoritative.
  }
}

/** Coerce an unknown into a clean `QuoteSlide[]` (defensive against stale data). */
function sanitizeQuoteSlides(raw: unknown): QuoteSlide[] {
  if (!Array.isArray(raw)) return [];
  const out: QuoteSlide[] = [];
  for (const s of raw) {
    if (!s || typeof s !== "object") continue;
    const rec = s as Record<string, unknown>;
    if (typeof rec.id !== "string" || !rec.id) continue;
    const quotes = sanitizeQuotes(rec.quotes);
    if (quotes.length === 0) continue; // a slide needs at least one quote
    const slide: QuoteSlide = { id: rec.id, quotes };
    if (typeof rec.title === "string" && rec.title.trim()) slide.title = rec.title;
    if (typeof rec.fontPx === "number" && Number.isFinite(rec.fontPx)) {
      slide.fontPx = rec.fontPx;
    }
    out.push(slide);
  }
  return out;
}

function readLocalQuoteSlides(slug: string): QuoteSlide[] {
  const s = store();
  if (!s) return [];
  try {
    const raw = s.getItem(quoteSlidesKeyFor(slug));
    if (!raw) return [];
    return sanitizeQuoteSlides(JSON.parse(raw));
  } catch {
    return [];
  }
}

function writeLocalQuoteSlides(slug: string, slides: QuoteSlide[]): void {
  const s = store();
  if (!s) return;
  try {
    s.setItem(quoteSlidesKeyFor(slug), JSON.stringify(slides));
  } catch {
    // Quota / private mode — ignore; the server copy is authoritative.
  }
}

/**
 * Replace the in-memory order + quotes caches from the server's TOUR# records
 * (called after GET /tours). Server is authoritative, so each record's order and
 * quotes overwrite the local write-through caches for its slug.
 */
export function hydrateOrders(
  tours: ReadonlyArray<{
    slug?: string;
    order?: readonly string[];
    quotes?: readonly unknown[];
    quoteSlides?: readonly unknown[];
  }>,
): void {
  serverOrders.clear();
  serverQuotes.clear();
  serverQuoteSlides.clear();
  for (const t of tours) {
    if (!t.slug) continue;
    const order = Array.isArray(t.order)
      ? t.order.filter((x): x is string => typeof x === "string")
      : [];
    serverOrders.set(t.slug, order);
    writeLocal(t.slug, order);

    const quotes = sanitizeQuotes(t.quotes);
    serverQuotes.set(t.slug, quotes);
    writeLocalQuotes(t.slug, quotes);

    const slides = sanitizeQuoteSlides(t.quoteSlides);
    serverQuoteSlides.set(t.slug, slides);
    writeLocalQuoteSlides(t.slug, slides);
  }
}

/**
 * The saved artifact-id order for a tour: the server-hydrated order if we have a
 * record for it, else the localStorage write-through (covers the window before a
 * tour's first persist). Ids are NOT validated against current membership —
 * callers reconcile (tours.ts `orderTourSlides`).
 */
export function getOrder(slug: string): string[] {
  const fromServer = serverOrders.get(slug);
  if (fromServer) return fromServer;
  return readLocal(slug);
}

/**
 * Record a new order locally (in-memory + localStorage write-through). The durable
 * DynamoDB write is done separately by useTours (PUT /tours/{slug}); this keeps the
 * synchronous render path instant and resilient.
 */
export function setOrder(slug: string, orderedIds: string[]): void {
  serverOrders.set(slug, orderedIds);
  writeLocal(slug, orderedIds);
}

/**
 * The saved quotes for a tour: the server-hydrated quotes if we have a record for
 * it, else the localStorage write-through (covers the window before a tour's first
 * quote persist). Mirrors getOrder's resilience model.
 */
export function getQuotes(slug: string): Quote[] {
  const fromServer = serverQuotes.get(slug);
  if (fromServer) return fromServer;
  return readLocalQuotes(slug);
}

/**
 * Record a tour's quotes locally (in-memory + localStorage write-through). The
 * durable DynamoDB write is done separately by useTours (PUT /tours/{slug}); this
 * keeps the panel instant and resilient if the tours API hiccups.
 */
export function setQuotesCache(slug: string, quotes: Quote[]): void {
  serverQuotes.set(slug, quotes);
  writeLocalQuotes(slug, quotes);
}

/**
 * The saved quote slides for a tour: the server-hydrated copy if we have a record
 * for it, else the localStorage write-through (covers the window before the first
 * persist). Mirrors getQuotes' resilience model.
 */
export function getQuoteSlides(slug: string): QuoteSlide[] {
  const fromServer = serverQuoteSlides.get(slug);
  if (fromServer) return fromServer;
  return readLocalQuoteSlides(slug);
}

/**
 * Record a tour's quote slides locally (in-memory + localStorage write-through). The
 * durable DynamoDB write is done separately by useTours (PUT /tours/{slug}).
 */
export function setQuoteSlidesCache(slug: string, slides: QuoteSlide[]): void {
  serverQuoteSlides.set(slug, slides);
  writeLocalQuoteSlides(slug, slides);
}

/**
 * Slugs that currently have at least one quote slide in the cache. Lets tour
 * derivation surface a tour that has quote slides but no tagged artworks yet.
 */
export function slugsWithQuoteSlides(): string[] {
  const out: string[] = [];
  for (const [slug, slides] of serverQuoteSlides) {
    if (slides.length > 0) out.push(slug);
  }
  return out;
}

/** Forget the saved order for a tour. Best-effort. */
export function clearOrder(slug: string): void {
  serverOrders.delete(slug);
  const s = store();
  if (!s) return;
  try {
    s.removeItem(keyFor(slug));
  } catch {
    // ignore
  }
}
